import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const AGREEMENT = `Teacher4Me Training Partner Agreement (v1)

By e-signing below, you (the applicant) acknowledge that:

1. Your organization will publish courses on Teacher4Me under a dual-brand certificate.
2. You are responsible for the accuracy and legality of your course content.
3. Content flagged by our compliance system (regulated or medical claims) requires admin review before publish.
4. Storage is metered — 500 MB starter quota per partner (raise available on request).
5. Revenue share is tracked manually. Payouts are subject to a separate written agreement.
6. Teacher4Me may suspend or reject partners at any time for non-compliance with Canadian consumer or education law.
7. You have not been debarred by any professional regulator in the past 5 years.

Signature must match the contact name exactly (case-insensitive).`;

export default function PartnerApply() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    organization_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    website: "",
    country: "Canada",
    province: "",
    about: "",
    accreditations: "",
    password: "",
    signature_full_name: "",
    accept: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept) return toast.error("Please accept the agreement");
    if (form.signature_full_name.trim().toLowerCase() !== form.contact_name.trim().toLowerCase()) {
      return toast.error("Signature must match contact name");
    }
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      const payload = {
        organization_name: form.organization_name.trim(),
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim().toLowerCase(),
        contact_phone: form.contact_phone.trim(),
        website: form.website.trim(),
        country: form.country,
        province: form.province,
        about: form.about,
        accreditations: form.accreditations.split(",").map((s) => s.trim()).filter(Boolean),
        password: form.password,
        signature_full_name: form.signature_full_name.trim(),
      };
      await api.post("/api/partners/apply", payload);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] py-16 px-6">
        <Card className="max-w-2xl mx-auto" data-testid="partner-apply-success">
          <CardHeader>
            <CardTitle className="text-2xl">Application received</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p>Thanks — we've received your application for <b>{form.organization_name}</b>.</p>
            <p>Our team will review within 3 business days. You'll receive a confirmation email once approved. You can already sign in but the dashboard is locked until approval.</p>
            <div className="flex gap-3 pt-4">
              <Button data-testid="partner-apply-goto-login" onClick={() => navigate("/login")}>Sign in</Button>
              <Button data-testid="partner-apply-back-home" variant="outline" onClick={() => navigate("/")}>Back to home</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0] py-12 px-6">
      <Card className="max-w-3xl mx-auto" data-testid="partner-apply-form-card">
        <CardHeader>
          <CardTitle className="text-2xl">Become a Teacher4Me Training Partner</CardTitle>
          <p className="text-sm text-gray-600 mt-1">
            Submit your application. We review within 3 business days.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Organization name *</Label>
                <Input required data-testid="partner-apply-org-input"
                  value={form.organization_name} onChange={update("organization_name")} />
              </div>
              <div>
                <Label>Website</Label>
                <Input data-testid="partner-apply-website-input"
                  placeholder="https://" value={form.website} onChange={update("website")} />
              </div>
              <div>
                <Label>Primary contact name *</Label>
                <Input required data-testid="partner-apply-contact-name-input"
                  value={form.contact_name} onChange={update("contact_name")} />
              </div>
              <div>
                <Label>Primary contact email *</Label>
                <Input required type="email" data-testid="partner-apply-email-input"
                  value={form.contact_email} onChange={update("contact_email")} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="partner-apply-phone-input"
                  value={form.contact_phone} onChange={update("contact_phone")} />
              </div>
              <div>
                <Label>Province / State</Label>
                <Input data-testid="partner-apply-province-input"
                  value={form.province} onChange={update("province")} />
              </div>
            </div>

            <div>
              <Label>About your organization (200-1000 chars)</Label>
              <Textarea rows={5} data-testid="partner-apply-about-input"
                value={form.about} onChange={update("about")} />
            </div>

            <div>
              <Label>Accreditations (comma-separated)</Label>
              <Input data-testid="partner-apply-accreditations-input"
                placeholder="e.g. College of Alberta Physiotherapists, CE-approved"
                value={form.accreditations} onChange={update("accreditations")} />
            </div>

            <div>
              <Label>Choose a password (min 8 chars) *</Label>
              <Input required type="password" minLength={8}
                data-testid="partner-apply-password-input"
                value={form.password} onChange={update("password")} />
              <p className="text-xs text-gray-500 mt-1">
                We'll create your partner login with the email above.
              </p>
            </div>

            <div className="border rounded-md p-4 bg-gray-50">
              <pre className="whitespace-pre-wrap text-xs text-gray-700 font-mono max-h-48 overflow-y-auto">
{AGREEMENT}
              </pre>
              <div className="mt-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" required
                    data-testid="partner-apply-accept-checkbox"
                    checked={form.accept} onChange={update("accept")}
                    className="mt-1" />
                  <span className="text-sm">I have read and accept the Training Partner Agreement (v1).</span>
                </label>
              </div>
              <div className="mt-3">
                <Label>Type your full legal name as e-signature *</Label>
                <Input required
                  data-testid="partner-apply-signature-input"
                  placeholder="Must match the contact name above"
                  value={form.signature_full_name} onChange={update("signature_full_name")} />
              </div>
            </div>

            <Button type="submit" disabled={busy}
              data-testid="partner-apply-submit-btn"
              className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white py-6">
              {busy ? "Submitting..." : "Submit application"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
